/**
 * Shared helpers for the two screens that let someone pick a Twilio number to buy (2026-09-11):
 * the A2P wizard's texting-number step and the WhatsApp wizard's number step. Import-free, so the
 * node-tests execute this exact file (cypress/node-tests/phone-number-picker.test.js).
 *
 * NO PRICE HERE, AND NONE ON EITHER SCREEN (Saumil, 2026-09-11): Teamio includes the number — the
 * practice is never charged Twilio's monthly fee — so a figure next to a number would be quoting a
 * cost nobody pays. Do not add a price formatter to this file.
 */

/**
 * A US number the way people read it: "+1 (561) 555-0123". Anything else comes back as given.
 * @param {string} e164
 * @returns {string}
 */
export function formatUsNumber(e164) {
  const raw = String(e164 == null ? '' : e164).trim();
  const d = raw.replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return raw;
}

/**
 * Where a number is: "West Palm Beach, FL", just "FL", or '' when Twilio gave neither.
 *
 * A region of "US" is the COUNTRY, not a place — Twilio sends it for some numbers with no locality
 * (seen live on dev 2026-09-11: the page printed "… in US"). It is dropped here because the A2P
 * step shows Twilio's fields as-is; the WhatsApp endpoint already drops it server-side.
 *
 * @param {{ locality?: string, region?: string }} n
 * @returns {string}
 */
export function numberPlace(n) {
  if (!n || typeof n !== 'object') return '';
  const clean = (v) => (typeof v === 'string' ? v.trim() : '');
  const region = clean(n.region).toUpperCase() === 'US' ? '' : clean(n.region);
  return [clean(n.locality), region].filter(Boolean).join(', ');
}

/**
 * A US area code as typed, or '' — three digits, first 2–9, not an N11 service code (211 … 911).
 *
 * MIRROR, kept in step with the server's own check:
 *   SOURCE: teamio-serverless-api/conversations-service/lib/waOnboarding/numberOptions.js
 *           normalizeAreaCode — read 2026-09-11.
 * The server re-checks (it answers `invalid_area_code`); this exists so a typo is caught on the
 * screen instead of costing a round trip.
 *
 * @param {*} value
 * @returns {string}
 */
export function normalizeAreaCode(value) {
  let s;
  if (typeof value === 'number' && Number.isInteger(value)) s = String(value);
  else if (typeof value === 'string') s = value.trim();
  else return '';
  if (!/^[2-9]\d{2}$/.test(s)) return '';
  if (/^[2-9]11$/.test(s)) return '';
  return s;
}

/** Shown under an area-code box when what was typed cannot be an area code. */
export const AREA_CODE_HINT_ERROR = 'Enter a three-digit area code, like 561.';
